import {
  BottomSheet,
  BottomSheetHeader,
  DialogFooter,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { toError } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { useCreateInterviewAttendanceVerification } from "../../api/useCreateInterviewAttendanceVerification";
import { VerifyInterviewAttendanceConfirmation } from "./VerifyInterviewAttendanceConfirmation";
import {
  type InterviewAttendanceVerificationFormData,
  interviewAttendanceVerificationFormSchema,
  VerifyInterviewAttendanceForm,
} from "./VerifyInterviewAttendanceForm";

interface VerifyInterviewAttendanceBottomSheetProps {
  modalState: UseModalState;
  interviewId: string;
  onSuccess: () => void;
}

export function VerifyInterviewAttendanceBottomSheet(
  props: VerifyInterviewAttendanceBottomSheetProps
) {
  const { modalState, interviewId, onSuccess } = props;
  const { showErrorToast } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);

  useLogEffect(APP_V2_APP_EVENTS.INTERVIEW_ATTENDANCE_VERIFICATION_OPENED, {
    interviewId,
  });

  const handleClose = () => {
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_ATTENDANCE_VERIFICATION_CLOSED, {
      interviewId,
      wasSubmitted: showConfirmation,
    });
    setShowConfirmation(false);
    modalState.closeModal();
  };

  const { mutateAsync: createVerification, isLoading: isCreatingVerification } =
    useCreateInterviewAttendanceVerification();

  const formMethods = useForm<InterviewAttendanceVerificationFormData>({
    resolver: zodResolver(interviewAttendanceVerificationFormSchema),
    defaultValues: {
      interviewerName: "",
      attendanceDescription: "",
      evidenceFile: undefined,
    },
    mode: "onChange",
  });

  const submitForm = async (data: InterviewAttendanceVerificationFormData) => {
    try {
      await createVerification({
        interviewId,
        interviewerName: data.interviewerName,
        attendanceDescription: data.attendanceDescription,
        evidenceFile: data.evidenceFile,
      });
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_ATTENDANCE_VERIFICATION_SUBMITTED, {
        interviewId,
        interviewerName: data.interviewerName,
        attendanceDescription: data.attendanceDescription,
        hasEvidenceFile: data.evidenceFile !== undefined,
      });
      setShowConfirmation(true);
      onSuccess();
    } catch (error) {
      const errorMessage = toError(error);
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_ATTENDANCE_VERIFICATION_SUBMITTED_FAILURE, {
        interviewId,
        error: errorMessage,
        interviewerName: data.interviewerName,
        attendanceDescription: data.attendanceDescription,
        hasEvidenceFile: data.evidenceFile !== undefined,
      });
      showErrorToast("Failed to submit verification. Please try again.");
    }
  };

  return (
    <BottomSheet
      disableSwipeArea
      canDismiss={false}
      modalState={modalState}
      header={<BottomSheetHeader LinkComponent={Link} onClose={handleClose} />}
      footer={
        <DialogFooter LinkComponent={Link} orientation="vertical">
          {showConfirmation ? (
            <Button fullWidth variant="contained" size="large" onClick={handleClose}>
              Got it
            </Button>
          ) : (
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              disabled={!formMethods.formState.isValid}
              isLoading={isCreatingVerification}
              endIconType="plane"
              onClick={formMethods.handleSubmit(submitForm)}
            >
              Submit verification
            </LoadingButton>
          )}
        </DialogFooter>
      }
      onClose={handleClose}
    >
      <Box sx={{ px: 6, py: 4 }}>
        {showConfirmation ? (
          <VerifyInterviewAttendanceConfirmation />
        ) : (
          <FormProvider {...formMethods}>
            <VerifyInterviewAttendanceForm />
          </FormProvider>
        )}
      </Box>
    </BottomSheet>
  );
}
